<template>
  <div class="auth-page">
    <div class="auth-container">
      <div class="auth-card">
        <div class="auth-card-left">
          <div class="auth-logo">
            <img src="@/assets/images/logo-dark.png" alt="HOVA" />
          </div>

          <div class="auth-form-wrapper">
            <LoginSkeleton v-if="loadingUserInfo" />
            <div class="auth-form-container" v-else>
              <div class="auth-form-header">
                <h1>{{ showOTPCode ? "Verify Code" : "Welcome" }}</h1>
                <p>
                  {{
                    showOTPCode
                      ? `We sent a code to ${providerPhoneHint || phoneNumber || "your phone"}`
                      : "Sign in to your store dashboard"
                  }}
                </p>
              </div>

              <div class="auth-form">
            <div v-if="providerAccountConflict" class="auth-alert">
              <hs-icon name="info" :size="16" />
              <span>
                {{ providerAccountConflict }} Sign in with your existing method,
                then link Google under Account Security.
              </span>
            </div>

            <label v-if="!providerPendingToken" class="auth-field">
              <span>Phone number</span>
              <vue-tel-input
                ref="phoneInput"
                class="auth-phone-input"
                :class="{
                  'auth-phone-input--valid': isValid && phone.length > 4,
                  'auth-phone-input--error': !isValid && phone.length > 4,
                }"
                v-model="phone"
                :dynamicPlaceholder="true"
                :inputOptions="{ showDialCode: true }"
                :validCharactersOnly="true"
                :mode="mode"
                autofocus
                @keyup.enter="isValid && !showOTPCode ? requestSMSVerify() : ''"
                v-bind="bindProps"
                @validate="handlePhoneInput"
                :disabled="showOTPCode"
              />
              <small v-show="!isValid && phone.length > 4">
                Enter a valid phone number.
              </small>
            </label>

            <transition name="auth-fade">
              <div v-if="showOTPCode" class="auth-otp">
                <div class="auth-otp__topline">
                  <span>Verification code</span>
                  <button type="button" @click="hideOTPCode" class="auth-otp__change">
                    {{ providerPendingToken ? "Cancel" : "Change phone" }}
                  </button>
                </div>

                <div class="otp-container">
                  <v-otp-input
                    ref="otpInput"
                    input-classes="otp-input"
                    separator=" "
                    :num-inputs="6"
                    :should-auto-focus="true"
                    input-type="letter-numeric"
                    :value="otpValue"
                    @on-change="handleOtpChange"
                    @on-complete="handleOnComplete"
                  />
                </div>

                <div
                  class="auth-resend"
                  v-if="!resend && !providerPendingToken"
                >
                  <span v-if="countdownTime > 0">
                    Resend available in <strong>{{ formattedCountdown }}</strong>
                  </span>
                  <span v-else>
                    Didn't receive code?
                    <button type="button" @click="requestSMSVerify(true)" class="auth-resend__link">
                      Resend it
                    </button>
                  </span>
                </div>
              </div>
            </transition>

            <hs-button
              v-if="!showOTPCode"
              block
              size="lg"
              icon="arrow-right"
              :loading="isLoading"
              :disabled="!isValid"
              @click="requestSMSVerify"
              class="auth-btn-primary"
            >
              Continue
            </hs-button>

            <div v-else class="auth-status">
              <hs-icon name="loader" :size="16" spin v-if="isLoading" />
              <hs-icon name="check-circle" :size="16" v-else />
              <span>{{ isLoading ? "Verifying your code..." : "Code verifies automatically when complete." }}</span>
            </div>

            <div v-if="!showOTPCode" class="auth-divider">
              <span>OR</span>
            </div>

            <button
              v-if="!showOTPCode"
              @click="signInWithGoogle"
              :disabled="isGoogleLoading"
              class="auth-btn-google"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span>{{ isGoogleLoading ? "Signing in..." : "Continue with Google" }}</span>
            </button>

              <div class="auth-build-info" title="Application version and source commit">
                {{ buildLabel }}
              </div>
              </div>
            </div>
          </div>
        </div>

        <div class="auth-card-right">
          <canvas ref="animationCanvas" class="auth-canvas"></canvas>
          <div class="auth-content">
            <!-- Stepper Section (Always Visible) -->
            <div class="stepper-section">
              <div class="stepper-header">
                <h3>Manage Everything in One Place</h3>
                <p>Access powerful tools to grow your business</p>
              </div>

              <div class="vertical-stepper">
                <div v-for="(step, index) in onboardingSteps" :key="index" class="stepper-step" :class="{ active: currentStep === index, completed: currentStep > index }">
                  <div class="step-indicator">
                    <div class="step-circle">
                      <span v-if="currentStep > index" class="step-check">✓</span>
                      <span v-else>{{ index + 1 }}</span>
                    </div>
                    <div v-if="index < onboardingSteps.length - 1" class="step-line"></div>
                  </div>
                  <div class="step-content-vertical">
                    <h4>{{ step.title }}</h4>
                    <p>{{ step.description }}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/vue-tel-input.css";
import VOtpInput from "vue3-otp-input";
import { toast } from "vue3-toastify";
import LoginSkeleton from "@/skeletons/LoginSkeleton";
import { HsButton, HsIcon } from "@/components/ui";
import { buildLabel } from "@/utils/buildInfo";

class AnimationCanvas {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = this.canvas.getContext('2d');
    this.particles = [];
    this.nodes = [];
    this.resize();
    this.init();
    this.animate();
    window.addEventListener('resize', () => this.resize());
  }

  resize() {
    this.canvas.width = this.canvas.offsetWidth;
    this.canvas.height = this.canvas.offsetHeight;
  }

  init() {
    for (let i = 0; i < 5; i++) {
      this.particles.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        radius: Math.random() * 10 + 5,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.2 + 0.3
      });
    }

    for (let i = 0; i < 10; i++) {
      this.nodes.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1.5
      });
    }
  }

  drawBubbles() {
    this.particles.forEach(particle => {
      particle.x += particle.vx;
      particle.y += particle.vy;

      if (particle.x + particle.radius > this.canvas.width) {
        particle.x = -particle.radius;
      }
      if (particle.x - particle.radius < 0) {
        particle.x = this.canvas.width + particle.radius;
      }
      if (particle.y + particle.radius > this.canvas.height) {
        particle.y = -particle.radius;
      }
      if (particle.y - particle.radius < 0) {
        particle.y = this.canvas.height + particle.radius;
      }

      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      this.ctx.fillStyle = `rgba(156, 163, 175, ${particle.opacity})`;
      this.ctx.fill();

      this.ctx.strokeStyle = `rgba(156, 163, 175, ${particle.opacity * 0.6})`;
      this.ctx.lineWidth = 1;
      this.ctx.stroke();
    });
  }

  drawNetwork() {
    this.nodes.forEach(node => {
      node.x += node.vx;
      node.y += node.vy;

      if (node.x < 0 || node.x > this.canvas.width) node.vx *= -1;
      if (node.y < 0 || node.y > this.canvas.height) node.vy *= -1;

      node.x = Math.max(0, Math.min(node.x, this.canvas.width));
      node.y = Math.max(0, Math.min(node.y, this.canvas.height));
    });

    for (let i = 0; i < this.nodes.length; i++) {
      for (let j = i + 1; j < this.nodes.length; j++) {
        const dx = this.nodes[i].x - this.nodes[j].x;
        const dy = this.nodes[i].y - this.nodes[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 150) {
          this.ctx.beginPath();
          this.ctx.moveTo(this.nodes[i].x, this.nodes[i].y);
          this.ctx.lineTo(this.nodes[j].x, this.nodes[j].y);
          this.ctx.strokeStyle = `rgba(156, 163, 175, ${0.15 * (1 - distance / 150)})`;
          this.ctx.lineWidth = 1;
          this.ctx.stroke();
        }
      }
    }

    this.nodes.forEach(node => {
      this.ctx.beginPath();
      this.ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
      this.ctx.fillStyle = 'rgba(156, 163, 175, 0.4)';
      this.ctx.fill();
    });
  }

  animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.drawBubbles();
    this.drawNetwork();
    requestAnimationFrame(() => this.animate());
  }
}

export default {
  name: "PhoneLoginView",
  components: {
    HsButton,
    HsIcon,
    VueTelInput,
    VOtpInput,
    LoginSkeleton,
  },
  data() {
    return {
      phone: "",
      showOTPCode: false,
      requestData: {},
      mode: "international",
      otpValue: "",
      bindProps: {
        placeholder: "Enter your phone number",
        defaultCountry: "RW",
        wrapperClasses: "",
        inputClasses: "",
        dropdownOptions: { showSearchBox: true, showFlags: true },
        inputOptions: { showDialCode: true },
      },
      isValid: false,
      phoneNumber: "",
      totalSeconds: 60,
      countdownTime: 0,
      interval: null,
      resend: false,
      isLoading: false,
      buildLabel,
      isGoogleLoading: false,
      providerPendingToken: "",
      providerPhoneHint: "",
      providerAccountConflict: "",
      animation: null,
      showOnboarding: false,
      currentStep: 0,
      stepCycleInterval: null,
      onboardingSteps: [
        {
          title: "Real-Time Analytics",
          description: "Monitor your sales, inventory, and business metrics in real-time from your dashboard"
        },
        {
          title: "Inventory Management",
          description: "Track stock levels, manage orders, and optimize your inventory across all locations"
        },
        {
          title: "Team Collaboration",
          description: "Manage your team, assign roles, and collaborate seamlessly with your staff"
        },
      ],
      selectedStore: null,
      stores: [
        { id: 1, name: "Main Store", location: "Downtown" },
        { id: 2, name: "North Branch", location: "Uptown" },
        { id: 3, name: "East Market", location: "East Side" },
      ],
      selectedRoles: [],
      roles: [
        { id: "manager", name: "Manager", description: "Full access" },
        { id: "staff", name: "Staff", description: "Limited access" },
        { id: "viewer", name: "Viewer", description: "Read-only access" },
      ],
      newTeamEmail: "",
      newTeamRole: "staff",
      teamMembers: [],
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isLoading;
    },
    loadingUserInfo() {
      return this.$store.getters.isLoadingUser;
    },
    formattedCountdown() {
      const hours = Math.floor(this.countdownTime / 3600);
      const minutes = Math.floor((this.countdownTime % 3600) / 60);
      const remainingSeconds = this.countdownTime % 60;

      const formattedHours = this.formatTime(hours);
      const formattedMinutes = this.formatTime(minutes);
      const formattedSeconds = this.formatTime(remainingSeconds);

      if (hours > 0) {
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
      } else {
        return `${formattedMinutes}:${formattedSeconds}`;
      }
    },
  },
  methods: {
    handlePhoneInput(value) {
      this.isValid = value.valid;
      this.phoneNumber = value.number;
    },
    formatTime(time) {
      return time < 10 ? `0${time}` : time;
    },
    startCountdown() {
      clearInterval(this.interval);
      this.countdownTime = this.totalSeconds;

      this.interval = setInterval(() => {
        if (this.countdownTime <= 0) {
          clearInterval(this.interval);
        } else {
          this.countdownTime--;
        }
      }, 1000);
    },
    requestSMSVerify(hasResend = false) {
      this.isLoading = true;
      const payload = { phone_number: this.phoneNumber };
      if (hasResend) this.resend = true;
      this.$store
        .dispatch("requestSMS", payload)
        .then((response) => {
          this.isLoading = false;
          this.requestData = response;
          this.showOTPCode = true;
          this.startCountdown();
          toast.success("OTP sent on your phone number");
          if (hasResend) this.resend = false;
        })
        .catch(() => {
          this.isLoading = false;
          if (hasResend) this.resend = false;
          toast.error(this.$store.getters.getError);
        });
    },
    hideOTPCode() {
      this.showOTPCode = false;
      this.otpValue = "";
      this.providerPendingToken = "";
      this.providerPhoneHint = "";
    },
    handleOtpChange(value) {
      this.otpValue = value;
    },
    async handleOnComplete(value) {
      this.isLoading = true;
      this.otpValue = value;
      try {
        if (this.providerPendingToken) {
          const response = await this.$store.dispatch("verifyProvider2FA", {
            pending_token: this.providerPendingToken,
            sms_code: `${value}`,
          });
          this.completeProviderAuthentication(response);
          return;
        }

        await this.$store.dispatch("verifyOTP", {
          ...this.requestData,
          sms_code: `${value}`,
        });
        await this.$store.dispatch("getUserInfo");
        this.redirectAfterAuthentication();
      } catch (error) {
        toast.error(
          error?.data?.message ||
            this.$store.getters.getError ||
            "Verification failed. Please try again.",
        );
      } finally {
        this.isLoading = false;
      }
    },
    async signInWithGoogle() {
      this.isGoogleLoading = true;
      this.providerAccountConflict = "";
      try {
        const { GoogleAuthProvider, signInWithPopup, getAuth } =
          await import("firebase/auth");
        const auth = getAuth();
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const idToken = credential?.idToken;
        if (!idToken) {
          throw new Error("Google did not return an identity token.");
        }

        const response = await this.$store.dispatch("googleLogin", {
          google_id_token: idToken,
        });

        if (
          response.status === "authenticated" ||
          response.status === "created"
        ) {
          this.completeProviderAuthentication(response);
        } else if (response.status === "account_exists") {
          this.providerAccountConflict =
            response.message ||
            "An account with this email already exists.";
          toast.info(this.providerAccountConflict);
        } else if (response.status === "2fa_required") {
          if (!response.pending_token) {
            throw new Error("The server did not return a pending 2FA token.");
          }
          this.providerPendingToken = response.pending_token;
          this.providerPhoneHint = response.phone_number_hint || "";
          this.showOTPCode = true;
          this.startCountdown();
          toast.info("Enter the SMS code to complete Google sign-in.");
        } else {
          throw new Error("Unexpected Google sign-in response.");
        }
      } catch (error) {
        const errorMessage =
          error?.data?.message ||
          error?.message ||
          "Google sign-in failed. Please try again.";
        toast.error(errorMessage);
      } finally {
        this.isGoogleLoading = false;
      }
    },
    completeProviderAuthentication(response) {
      if (!response?.token || !response?.user) {
        throw new Error("The server returned an incomplete sign-in response.");
      }
      this.redirectAfterAuthentication();
    },
    redirectAfterAuthentication() {
      const requestedRedirect = this.$route.query.redirect;
      const destination =
        typeof requestedRedirect === "string" &&
        requestedRedirect.startsWith("/") &&
        !requestedRedirect.startsWith("//")
          ? requestedRedirect
          : "/store";
      this.$router.push(destination);
    },
    nextOnboardingStep() {
      if (this.currentStep === 0 && !this.selectedStore) {
        toast.error("Please select a store");
        return;
      }
      if (this.currentStep === 1 && this.selectedRoles.length === 0) {
        toast.error("Please select at least one role");
        return;
      }
      if (this.currentStep === 2) {
        const requestedRedirect = this.$route.query.redirect;
        const destination =
          typeof requestedRedirect === "string" &&
          requestedRedirect.startsWith("/") &&
          !requestedRedirect.startsWith("//")
            ? requestedRedirect
            : "/store";
        this.$router.push(destination);
        return;
      }
      this.currentStep++;
    },
    prevOnboardingStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    addTeamMember() {
      if (this.newTeamEmail && this.newTeamRole) {
        this.teamMembers.push({
          email: this.newTeamEmail,
          role: this.newTeamRole,
        });
        this.newTeamEmail = "";
        this.newTeamRole = "staff";
      }
    },
  },
  beforeUnmount() {
    clearInterval(this.interval);
    clearInterval(this.stepCycleInterval);
  },
  mounted() {
    document.title = "Login | HovaStore";

    // Auto-cycle stepper steps
    this.stepCycleInterval = setInterval(() => {
      if (this.currentStep < this.onboardingSteps.length - 1) {
        this.currentStep++;
      } else {
        this.currentStep = 0;
      }
    }, 4500);

    this.$nextTick(() => {
      const canvas = this.$refs.animationCanvas;
      if (canvas) {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        this.animation = new AnimationCanvas(canvas);
      }
    });

    // Make only country code input readonly (inside dropdown, not phone number input)
    // And focus the phone number input
    setTimeout(() => {
      const dropdown = document.querySelector('.auth-phone-input .vti__dropdown');
      if (dropdown) {
        const countryCodeInput = dropdown.querySelector('.vti__input');
        if (countryCodeInput) {
          countryCodeInput.setAttribute('readonly', 'readonly');
          countryCodeInput.style.cursor = 'default';
        }
      }

      // Focus the phone number input field
      const phoneInputField = document.querySelector('.auth-phone-input .vti__input:not([readonly])');
      if (phoneInputField) {
        phoneInputField.focus();
      }
    }, 100);
  },
};
</script>

<style scoped>
.auth-build-info {
  color: #9ca3af;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.68rem;
  margin-top: 0.85rem;
  opacity: 0.72;
  text-align: center;
  white-space: nowrap;
}
</style>
